/**
 * Embed Top Moments snippet block.
 *
 * Single source of truth for the embed instructions. Used in:
 *   1. /settings → mounted inline at the bottom of Wall of Champions
 *   2. /dashboard → opened inside a Dialog from the Embed Performance tile
 *
 * Owns: clipboard copy, live snippet text, theme + limit variant toggles,
 * toast feedback.  Does NOT own: where the block is mounted, who's
 * allowed to see it, the slug source.
 *
 * Phase 266g — added Light/Dark theme + display-count variants. The widget
 * JS at /api/widget/top-moments.js reads data-theme + data-limit from the
 * host element, so the toggles produce snippets that actually render
 * differently when pasted.
 */
import { useState } from 'react';
import { Trophy, Sun, Moon, Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const DEFAULT_LIMIT = 12;
const MIN_LIMIT = 3;
const MAX_LIMIT = 24;

function buildSnippet({ backend, slug, theme, limit }) {
  // Only emit non-default attributes so the snippet stays clean for the
  // overwhelming majority of owners who'll pick the defaults.
  const attrs = [`data-academy="${slug || 'your-slug'}"`];
  if (theme === 'dark') attrs.push('data-theme="dark"');
  if (limit !== DEFAULT_LIMIT) attrs.push(`data-limit="${limit}"`);
  return `<div id="outplay-top-moments" ${attrs.join(' ')}></div>
<script src="${backend}/api/widget/top-moments.js" async></script>`;
}

export default function EmbedTopMomentsBlock({ slug }) {
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState('light');
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const snippet = buildSnippet({ backend, slug, theme, limit });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success('Embed code copied — paste it onto your academy website.');
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) {
      toast.error('Could not copy — please select the snippet manually.');
    }
  };

  const adjustLimit = (delta) => {
    setLimit((prev) => {
      const next = prev + delta;
      if (next < MIN_LIMIT || next > MAX_LIMIT) return prev;
      return next;
    });
  };

  return (
    <div
      className="rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/40 dark:border-amber-900/50 dark:bg-amber-950/20 p-4 space-y-3"
      data-testid="embed-top-moments-block"
    >
      <div className="flex items-start gap-2">
        <div className="rounded-md bg-amber-500/15 p-1.5">
          <Trophy className="h-4 w-4 text-amber-700 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-amber-900 dark:text-amber-200">
            Embed Top Moments on your own website
          </p>
          <p className="text-[11px] text-amber-800/80 dark:text-amber-200/80">
            Drop this <code className="font-mono">&lt;script&gt;</code> into any page (Wix, Wordpress, Squarespace, raw HTML). It pulls your latest moments live — no rebuild required.
          </p>
        </div>
      </div>

      {/* Phase 266g — variant toggles. The snippet below regenerates live. */}
      <div className="flex flex-wrap items-center gap-3 text-[11px]">
        <div className="flex items-center gap-1.5">
          <span className="font-bold text-amber-900/80 dark:text-amber-200/80">Theme</span>
          <div className="inline-flex rounded-md border border-amber-300 dark:border-amber-800 overflow-hidden" data-testid="embed-theme-toggle">
            <button
              type="button"
              onClick={() => setTheme('light')}
              className={`px-2 py-1 inline-flex items-center gap-1 transition-colors ${
                theme === 'light'
                  ? 'bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 font-bold'
                  : 'bg-transparent text-amber-700 dark:text-amber-300 hover:bg-amber-100/60 dark:hover:bg-amber-900/40'
              }`}
              data-testid="embed-theme-light"
              aria-pressed={theme === 'light'}
            >
              <Sun className="h-3 w-3" /> Light
            </button>
            <button
              type="button"
              onClick={() => setTheme('dark')}
              className={`px-2 py-1 inline-flex items-center gap-1 transition-colors ${
                theme === 'dark'
                  ? 'bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 font-bold'
                  : 'bg-transparent text-amber-700 dark:text-amber-300 hover:bg-amber-100/60 dark:hover:bg-amber-900/40'
              }`}
              data-testid="embed-theme-dark"
              aria-pressed={theme === 'dark'}
            >
              <Moon className="h-3 w-3" /> Dark
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <span className="font-bold text-amber-900/80 dark:text-amber-200/80">Moments</span>
          <div className="inline-flex items-center rounded-md border border-amber-300 dark:border-amber-800 overflow-hidden">
            <button
              type="button"
              onClick={() => adjustLimit(-1)}
              disabled={limit <= MIN_LIMIT}
              className="px-1.5 py-1 hover:bg-amber-100/60 dark:hover:bg-amber-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="embed-limit-decrement"
              aria-label="Decrease moment count"
            >
              <Minus className="h-3 w-3" />
            </button>
            <span
              className="px-2 py-1 font-mono font-bold tabular-nums min-w-[28px] text-center text-amber-900 dark:text-amber-100"
              data-testid="embed-limit-value"
            >
              {limit}
            </span>
            <button
              type="button"
              onClick={() => adjustLimit(+1)}
              disabled={limit >= MAX_LIMIT}
              className="px-1.5 py-1 hover:bg-amber-100/60 dark:hover:bg-amber-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="embed-limit-increment"
              aria-label="Increase moment count"
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>

      <pre
        className="text-[11px] font-mono leading-snug bg-slate-900 text-emerald-300 rounded-md p-3 overflow-x-auto whitespace-pre"
        data-testid="embed-top-moments-snippet"
      >{snippet}</pre>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] text-muted-foreground">
          Multiple widgets per page supported. <code className="font-mono">data-slug</code> works as an alias.
        </p>
        <Button
          size="sm"
          variant={copied ? 'default' : 'outline'}
          className={copied ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
          onClick={copy}
          data-testid="embed-top-moments-copy"
        >
          {copied ? 'Copied!' : 'Copy embed code'}
        </Button>
      </div>
    </div>
  );
}
